import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import { takeLatest, put, all, delay, takeEvery } from 'redux-saga/effects';
import history from '../../providers/history';
import { GetObject } from '../../providers/storage';
import { IAuthenticatedUser } from '../authentication/types';
import { fileUploadComplete } from '../photos/slice';
import { roomExportFlirRequestService, roomExportPhotosService, roomExportVideoRequestService, roomFloorPlanDeleteRequestService, roomFloorPlanSaveRequestService, roomFrequencyAnalysisDeleteRequestService, roomFrequencyAnalysisSaveRequestService, roomFrequencyFileDeleteRequestService, roomFrequencyFileSaveRequestService, roomsWorkRegisterRemoveRequestService, roomsWorkRegisterSaveRequestService, sweepArchiveService, sweepExportService, sweepsGuestRemoveRequestService, sweepsGuestSaveRequestService, sweepsListRequestService, sweepsRemoveRequestService, sweepsRoomItemRemoveRequestService, sweepsRoomItemSaveRequestService, sweepsRoomRemoveRequestService, sweepsRoomSaveRequestService, sweepsSaveRequestService, sweepsWorkRegisterRemoveRequestService, sweepsWorkRegisterSaveRequestService, sweepUnarchiveService } from './services';
import { roomExportPhotosFailure, roomExportPhotosFlirFailure, roomExportPhotosFlirRequest, roomExportPhotosFlirSuccess, roomExportPhotosRequest, roomExportPhotosSuccess, roomExportVideosFailure, roomExportVideosRequest, roomExportVideosSuccess, roomFloorPlanDeleteFailure, roomFloorPlanDeleteRequest, roomFloorPlanDeleteSuccess, roomFloorPlanSaveFailure, roomFloorPlanSaveRequest, roomFloorPlanSaveSuccess, roomFrequencyAnalysisDeleteFailure, roomFrequencyAnalysisDeleteRequest, roomFrequencyAnalysisDeleteSuccess, roomFrequencyAnalysisSaveFailure, roomFrequencyAnalysisSaveRequest, roomFrequencyAnalysisSaveSuccess, roomFrequencyFileDeleteFailure, roomFrequencyFileDeleteRequest, roomFrequencyFileDeleteSuccess, roomFrequencyFileSaveFailure, roomFrequencyFileSaveRequest, roomFrequencyFileSaveSuccess, roomSaveWorkRegisterFailure, roomSaveWorkRegisterRequest, roomSaveWorkRegisterSuccess, roomWorkRegisterRemoveFailure, roomWorkRegisterRemoveRequest, roomWorkRegisterRemoveSuccess, sweepAddMember, sweepArchive, sweepArchivedPagination, sweepArchiveFailure, sweepExport, sweepExportFailure, sweepExportSuccess, sweepGuestRemoveFailure, sweepGuestRemoveRequest, sweepGuestRemoveSuccess, sweepRemoveFailure, sweepRemoveRequest, sweepRemoveSuccess, sweepRoomItemRemoveFailure, sweepRoomItemRemoveRequest, sweepRoomItemRemoveSuccess, sweepRoomRemoveFailure, sweepRoomRemoveRequest, sweepRoomRemoveSuccess, sweepsListFailure, sweepsListRequest, sweepsListSuccess, sweepsSaveFailure, sweepsSaveGuestFailure, sweepsSaveGuestRequest, sweepsSaveGuestSuccess, sweepsSaveRequest, sweepsSaveRoomFailure, sweepsSaveRoomItemFailure, sweepsSaveRoomItemRequest, sweepsSaveRoomItemSuccess, sweepsSaveRoomRequest, sweepsSaveRoomSuccess, sweepsSaveSuccess, sweepsSaveWorkRegisterFailure, sweepsSaveWorkRegisterRequest, sweepsSaveWorkRegisterSuccess, sweepUnarchive, sweepUnarchiveFailure, sweepWorkRegisterRemoveFailure, sweepWorkRegisterRemoveRequest, sweepWorkRegisterRemoveSuccess } from "./slice";
import { IFloorPlanDeleteResponse, IFloorPlanSaveResponse, IFrequencyAnalysisDeleteResponse, IFrequencyAnalysisSaveResponse, IFrequencyFileSaveResponse, IPaginations, IRoomWorkRegisterRemoveRequestResponse, IRoomWorkRegisterRequestPayload, IRoomWorkRegisterRequestResponse, ISweep, ISweepGuestRemoveRequestResponse, ISweepMember, ISweepRemoveRequestResponse, ISweepRoom, ISweepRoomItem, ISweepRoomTime, ISweepSaveGuestRequestPayload, ISweepSaveGuestRequestResponse, ISweepSaveRequestPayload, ISweepSaveRequestResponse, ISweepSaveRoomItemRequestPayload, ISweepSaveRoomItemRequestResponse, ISweepSaveRoomRequestPayload, ISweepSaveRoomRequestResponse, ISweepTime, ISweepWorkRegisterRemoveRequestResponse, ISweepWorkRegisterRequestPayload, ISweepWorkRegisterRequestResponse, ISweepsResponse } from './types';

// *** SWEEPS LIST ***
function* sweepsListRequestSaga(action: PayloadAction<IPaginations>) {
    try {
        const sweepsResponse: AxiosResponse<ISweepsResponse> = yield sweepsListRequestService(action.payload);

        if (sweepsResponse.data.status) {
            const sweepsData: ISweep[] = sweepsResponse.data.data.map(sweep => ({
                id: sweep.id,
                name: sweep.name,
                customer: sweep.customer,
                address: sweep.address,
                reportNumber: sweep.reportNumber,
                version: sweep.version,
                archivedAt: sweep.archivedAt,
                times: sweep.times.map(time => ({
                    id: time.id,
                    beginingAt: new Date(time.beginingAt),
                    endingAt: new Date(time.endingAt),
                    observation: time.observations,
                    sweepId: sweep.id
                })),
                members: sweep.members.map(member => ({
                    id: member.id,
                    sweepId: sweep.id,
                    idUser: member.userId,
                    name: member.name,
                    role: member.role
                })).sort((a, b) => a.name.localeCompare(b.name)),
                rooms: sweep.rooms.map(room => ({
                    id: room.id,
                    name: room.name,
                    times: room.times.map(time => ({
                        id: time.id,
                        beginingAt: new Date(time.beginingAt),
                        endingAt: new Date(time.endingAt),
                        observation: time.observations,
                        roomId: room.id
                    })),
                    items: room.items.map(item => ({
                        id: item.id,
                        roomId: item.roomId,
                        name: item.name,
                        amount: parseInt(item.amount),
                        observations: item.observations,
                        info: item.info,
                        open: item.open,
                        sealed: item.sealed,
                        jeopardized: item.jeopardized,
                        risk: item.risk
                    })),
                    width: room.width,
                    height: room.height,
                    length: room.length,
                    metricUnit: room.metricUnit === 'm' ? 'm' : 'ft',
                    sweepId: room.sweepId,
                    floorPlan: room.floorPlan,
                    frequencyFile: room.frequencyFile,
                    frequencyAnalysis: room.frequencyAnalysis
                }))
            })) || []


            yield put(sweepsListSuccess({
                data: sweepsData,
                isArchived: action.payload.isArchived
            }));
            if (action.payload.isArchived) {
                yield put(sweepArchivedPagination({
                    count: sweepsResponse.data.count,
                    limit: sweepsResponse.data.limit,
                    currentPage: sweepsResponse.data.currentPage,
                    totalPages: sweepsResponse.data.totalPages,
                }))
            }
        }
        else {
            yield put(sweepsListFailure({
                error: {
                    message: sweepsResponse.data.message || 'Error trying to get the list of sweeps'
                },
                id: ''
            }));
        }
    }
    catch (error) {
        yield put(sweepsListFailure({
            error: {
                message: 'Error trying to get the list of sweeps'
            },
            id: ''
        }));
    }
}

function* watchSweepsListRequestSaga() {
    yield takeEvery(sweepsListRequest.toString(), sweepsListRequestSaga);
}

function* sweepSaveRequestSaga(action: PayloadAction<ISweep>) {
    try {
        const payloadSweep: ISweepSaveRequestPayload = {
            name: action.payload.name,
            customer: action.payload.customer,
            reportNumber: action.payload.reportNumber,
            address: action.payload.address,
            version: action.payload.version,
            archivedAt: action.payload.archivedAt
        }

        const sweepsSaveResponse: AxiosResponse<ISweepSaveRequestResponse> = yield sweepsSaveRequestService(payloadSweep, action.payload.id);

        if (sweepsSaveResponse.data.status) {
            history.back();

            yield put(sweepsSaveSuccess({
                ...action.payload,
                id: sweepsSaveResponse.data.data.id,
                name: sweepsSaveResponse.data.data.name,
                customer: sweepsSaveResponse.data.data.customer,
                reportNumber: sweepsSaveResponse.data.data.reportNumber,
                address: sweepsSaveResponse.data.data.address,
                version: sweepsSaveResponse.data.data.version
            }))
            yield put(sweepsListRequest({}))

            yield delay(50);

            if (!action.payload.id && sweepsSaveResponse.data.data.memberId) {
                history.push(`/sweep/${sweepsSaveResponse.data.data.id}`);

                const authenticatedUser = GetObject<IAuthenticatedUser>('authenticatedUser');

                if (authenticatedUser) {
                    yield put(sweepAddMember({
                        id: sweepsSaveResponse.data.data.memberId,
                        idUser: authenticatedUser.id,
                        name: authenticatedUser.name,
                        role: '',
                        sweepId: sweepsSaveResponse.data.data.id,
                        idSweep: sweepsSaveResponse.data.data.id
                    }))
                }
            }
        }
        else {
            yield put(sweepsSaveFailure({
                id: action.payload.id || "",
                error: {
                    message: sweepsSaveResponse.data.message || 'Error trying to save sweep'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepsSaveFailure({
            id: action.payload.id || "",
            error: {
                message: error.response.data.message || 'Error trying to get the list of sweeps'
            }
        }));
    }
}

function* watchSweepSaveRequestSaga() {
    yield takeEvery(sweepsSaveRequest.type, sweepSaveRequestSaga);
}

// ARCHIVE
function* sweepArquiveRequestSaga(action: PayloadAction<string>) {
    try {
        const sweepsArchiveResponse: AxiosResponse<ISweepSaveRequestResponse> = yield sweepArchiveService(action.payload);

        if (sweepsArchiveResponse.data.status) {
            yield put(sweepsListRequest({}))
            yield put(sweepsListRequest({
                limit: 24,
                page: 1,
                isArchived: true
            }))
        }
        else {
            yield put(sweepArchiveFailure({
                id: action.payload || "",
                error: {
                    message: sweepsArchiveResponse.data.message || 'Error trying to archive sweep'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepArchiveFailure({
            id: action.payload || "",
            error: {
                message: error.response.data.message || 'Error trying to archive sweep'
            }
        }));
    }
}

function* watchSweepArquiveRequestSaga() {
    yield takeEvery(sweepArchive.type, sweepArquiveRequestSaga);
}

// UNARCHIVE
function* sweepUnarquiveRequestSaga(action: PayloadAction<string>) {
    try {
        const sweepsArchiveResponse: AxiosResponse<ISweepSaveRequestResponse> = yield sweepUnarchiveService(action.payload);

        if (sweepsArchiveResponse.data.status) {
            yield put(sweepsListRequest({}))
            yield put(sweepsListRequest({
                limit: 24,
                page: 1,
                isArchived: true
            }))
        }
        else {
            yield put(sweepUnarchiveFailure({
                id: action.payload || "",
                error: {
                    message: sweepsArchiveResponse.data.message || 'Error trying to unarchive sweep'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepUnarchiveFailure({
            id: action.payload || "",
            error: {
                message: error.response.data.message || 'Error trying to unarchive sweep'
            }
        }));
    }
}

function* watchSweepUnarquiveRequestSaga() {
    yield takeEvery(sweepUnarchive.type, sweepUnarquiveRequestSaga);
}

// REMOVE
function* sweepsRemoveRequestSaga(action: PayloadAction<string>) {
    try {
        const roomRemoveResponse: AxiosResponse<ISweepRemoveRequestResponse> = yield sweepsRemoveRequestService(action.payload);

        if (roomRemoveResponse.data.status) {
            history.back();

            yield put(sweepRemoveSuccess(action.payload))
        }
        else {
            yield put(sweepRemoveFailure({
                sweepId: action.payload,
                error: {
                    message: roomRemoveResponse.data.message || 'Error trying to remove this sweep'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepRemoveFailure({
            sweepId: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to remove this sweep'
            }
        }));
    }
}

function* watchSweepRemoveRequest() {
    yield takeLatest(sweepRemoveRequest.type, sweepsRemoveRequestSaga);
}

// EXPORT
function* sweepExportRequestSaga(action: PayloadAction<string>) {
    try {
        const exportRequest: AxiosResponse<Blob> = yield sweepExportService(action.payload);

        let blob = new Blob([exportRequest.data], { type: exportRequest.headers['content-type'] })

        saveAs(blob)

        yield put(sweepExportSuccess(action.payload))
    }
    catch (error: any) {
        yield put(sweepExportFailure({
            id: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to export this sweep'
            }
        }));
    }
}

function* watchSweepExportRequest() {
    yield takeLatest(sweepExport.type, sweepExportRequestSaga);
}

// GUEST-MEMBERS
function* sweepsGuestSaveRequestSaga(action: PayloadAction<ISweepMember>) {
    try {
        const payloadGuest: ISweepSaveGuestRequestPayload = {
            guest: {
                id: action.payload.id,
                name: action.payload.name,
                role: action.payload.role,
                idUser: action.payload.idUser

            },
            sweepId: action.payload.sweepId
        }

        const guestSaveResponse: AxiosResponse<ISweepSaveGuestRequestResponse, ISweepSaveGuestRequestResponse> = yield sweepsGuestSaveRequestService(payloadGuest);

        if (guestSaveResponse.data.status) {
            history.back();

            yield put(sweepsSaveGuestSuccess({
                ...action.payload,
                id: guestSaveResponse.data.data.id || action.payload.id,
                name: guestSaveResponse.data.data.name,
                role: guestSaveResponse.data.data.role,
                sweepId: action.payload.sweepId
            }))
        }
        else {
            yield put(sweepsSaveGuestFailure({
                error: {
                    message: guestSaveResponse.data.message || 'Error trying to save sweep guest'
                }

            }));

        }
    }
    catch (error: any) {
        yield put(sweepsSaveGuestFailure({
            error: {
                message: error.response.data.message || 'Error trying to save sweep guest'
            }
        }));
    }
}

function* watchSweepGuestSaveRequest() {
    yield takeLatest(sweepsSaveGuestRequest.type, sweepsGuestSaveRequestSaga);
}

function* sweepsGuestRemoveRequestSaga(action: PayloadAction<string>) {
    try {
        const guestRemoveResponse: AxiosResponse<ISweepGuestRemoveRequestResponse> = yield sweepsGuestRemoveRequestService(action.payload);

        if (guestRemoveResponse.data.status) {
            yield put(sweepGuestRemoveSuccess(action.payload))
        }
        else {
            yield put(sweepGuestRemoveFailure({
                memberId: action.payload,
                error: {
                    message: guestRemoveResponse.data.message || 'Error trying to remove sweep guest'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepGuestRemoveFailure({
            memberId: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to remove sweep guest'
            }
        }));
    }
}

function* watchSweepGuestRemoveRequest() {
    yield takeLatest(sweepGuestRemoveRequest.type, sweepsGuestRemoveRequestSaga);
}

// ROOMS
function* sweepsRoomSaveRequestSaga(action: PayloadAction<ISweepRoom>) {
    try {
        const payloadRoom: ISweepSaveRoomRequestPayload = {
            name: action.payload.name,
            sweepId: action.payload.sweepId,
            height: action.payload.height,
            width: action.payload.width,
            length: action.payload.length,
            metricUnit: action.payload.metricUnit,
            floorPlan: action.payload.floorPlan,
            frequencyAnalysis: action.payload.frequencyAnalysis
        }

        const roomSaveResponse: AxiosResponse<ISweepSaveRoomRequestResponse> = yield sweepsRoomSaveRequestService(payloadRoom, action.payload.id);

        if (roomSaveResponse.data.status) {
            const responseData = roomSaveResponse.data.data;

            history.back();

            yield put(sweepsSaveRoomSuccess({
                ...action.payload,
                id: responseData.id,
                name: responseData.name,
                height: responseData.height,
                width: responseData.width,
                length: responseData.length,
                metricUnit: responseData.metricUnit,
                floorPlan: responseData.floorPlan,
                frequencyFile: responseData.frequencyFile,
                frequencyAnalysis: responseData.frequencyAnalysis,
                sweepId: action.payload.sweepId
            }))

            yield delay(50);

            if (!action.payload.id) {
                history.push(`/room/${action.payload.sweepId}/${responseData.id}`);
            }
        }
        else {
            yield put(sweepsSaveRoomFailure({
                error: {
                    message: roomSaveResponse.data.message || 'Error trying to save sweep room'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepsSaveRoomFailure({
            error: {
                message: error.response.data.message || 'Error trying to save sweep room'

            }
        }));
    }
}

function* watchSweepRoomSaveRequest() {
    yield takeLatest(sweepsSaveRoomRequest.type, sweepsRoomSaveRequestSaga);
}

function* sweepsRoomRemoveRequestSaga(action: PayloadAction<string>) {
    try {
        const roomRemoveResponse: AxiosResponse<ISweepSaveRoomRequestResponse> = yield sweepsRoomRemoveRequestService(action.payload);

        if (roomRemoveResponse.data.status) {
            history.back();

            yield put(sweepRoomRemoveSuccess(action.payload))
        }
        else {
            yield put(sweepRoomRemoveFailure({
                roomId: action.payload,
                error: {
                    message: roomRemoveResponse.data.message || 'Error trying to remove sweep room'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepRoomRemoveFailure({
            roomId: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to remove sweep room'
            }
        }));
    }
}

function* watchSweepRoomRemoveRequest() {
    yield takeLatest(sweepRoomRemoveRequest.type, sweepsRoomRemoveRequestSaga);
}
// EXPORT PHOTO
function* roomPhotosExportRequestSaga(action: PayloadAction<string>) {
    try {
        const exportRequest: AxiosResponse<Blob | { status: false, message: string }> = yield roomExportPhotosService(action.payload);

        if (exportRequest.data instanceof Blob) {
            let blob = new Blob([exportRequest.data], { type: exportRequest.headers['content-type'] })

            saveAs(blob)

            yield put(roomExportPhotosSuccess(action.payload))
        }
        else {
            yield put(roomExportPhotosFailure({
                id: action.payload,
                error: {
                    message: exportRequest.data.message || 'Error trying to export this room photos'
                }
            }));
        }

    }
    catch (error: any) {
        yield put(roomExportPhotosFailure({
            id: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to export this room photos'
            }
        }));
    }
}
function* watchRoomPhotoExportRequest() {
    yield takeLatest(roomExportPhotosRequest.type, roomPhotosExportRequestSaga);
}
// EXPORT PHOTO FLIR
function* roomPhotosFlirExportRequestSaga(action: PayloadAction<string>) {
    try {
        const exportRequest: AxiosResponse<Blob | { status: false, message: string }, Blob | { status: false, message: string }> = yield roomExportFlirRequestService(action.payload);

        if (exportRequest.data instanceof Blob) {
            let blob = new Blob([exportRequest.data], { type: exportRequest.headers['content-type'] })

            saveAs(blob)

            yield put(roomExportPhotosFlirSuccess(action.payload))
        }
        else {
            yield put(roomExportPhotosFlirFailure({
                id: action.payload,
                error: {
                    message: exportRequest.data.message || 'Error trying to export this room flir photos'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(roomExportPhotosFlirFailure({
            id: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to export this room flir photos'
            }
        }));
    }
}
function* watchRoomPhotoFlirExportRequest() {
    yield takeLatest(roomExportPhotosFlirRequest.type, roomPhotosFlirExportRequestSaga);
}

// *** EXPORT VIDEO *** 
function* roomVideosExportRequestSaga(action: PayloadAction<string>) {
    try {
        const exportRequest: AxiosResponse<Blob | { status: false, message: string }> = yield roomExportVideoRequestService(action.payload);

        if (exportRequest.data instanceof Blob) {
            let blob = new Blob([exportRequest.data], { type: exportRequest.headers['content-type'] })

            saveAs(blob)

            yield put(roomExportVideosSuccess(action.payload))
        }
        else {
            yield put(roomExportVideosFailure({
                id: action.payload,
                error: {
                    message: exportRequest.data.message || 'Error trying to export this room videos'
                }
            }));
        }

    }
    catch (error: any) {
        yield put(roomExportVideosFailure({
            id: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to export this room videos'
            }
        }));
    }
}
function* watchRoomVideoExportRequest() {
    yield takeLatest(roomExportVideosRequest.type, roomVideosExportRequestSaga);
}

// FLOOR PLAN *** SAVE ***
function* roomFloorPlanSaveRequestSaga(action: PayloadAction<{
    uploadId: string;
    sweepId: string;
    floorPlan: string;
    roomId: string;
}>) {
    try {
        const saveResponse: AxiosResponse<IFloorPlanSaveResponse> = yield roomFloorPlanSaveRequestService(
            action.payload.roomId,
            action.payload.floorPlan
        );
        if (saveResponse.data.status) {
            const data = saveResponse.data.data;

            yield put(fileUploadComplete({ uploadId: action.payload.uploadId }));
            yield put(roomFloorPlanSaveSuccess({
                sweepId: action.payload.sweepId,
                roomId: action.payload.roomId,
                floorPlan: data.floorPlan
            }));

        }

    } catch (error: any) {
        yield put(roomFloorPlanSaveFailure({
            sweepId: action.payload.sweepId,
            roomId: action.payload.roomId,
            error: {
                message: error.response.data.message || 'Error trying to save room floor Plan'
            }
        }));
    }
}
function* watchRoomFloorPLanSaveRequestSaga() {
    yield takeLatest(roomFloorPlanSaveRequest.toString(), roomFloorPlanSaveRequestSaga);
}
// FLOOR PLAN *** DELETE ***
function* roomDeleteFloorPlanSaga(action: PayloadAction<{
    sweepId: string;
    roomId: string;

}>) {
    try {
        const floorPlanDeleteResponse: AxiosResponse<IFloorPlanDeleteResponse> = yield roomFloorPlanDeleteRequestService(
            action.payload.roomId
        );

        if (floorPlanDeleteResponse.data.status) {
            yield put(roomFloorPlanDeleteSuccess({
                roomId: action.payload.roomId,
                sweepId: action.payload.sweepId,
            }))
        } else {
            yield put(roomFloorPlanDeleteFailure({
                roomId: action.payload.roomId,
                sweepId: action.payload.sweepId,
                error: {
                    message: floorPlanDeleteResponse.data.message || 'Error removing Floor Plan'
                },
            }))
        }
    } catch (error: any) {
        yield put(roomFloorPlanDeleteFailure({
            roomId: action.payload.roomId,
            sweepId: action.payload.roomId,
            error: {
                message: error.response.data.message || 'Error trying to get the floor plan'
            }
        }));
    }
}
function* watchDeleteFloorPlanRequestSaga() {
    yield takeLatest(roomFloorPlanDeleteRequest.toString(), roomDeleteFloorPlanSaga);
}
// FREQUENCY FILE *** SAVE ***
function* roomFrequencyFileSaveRequestSaga(action: PayloadAction<{
    uploadId: string;
    sweepId: string;
    frequencyFile: string;
    roomId: string;
}>) {
    try {
        const saveResponse: AxiosResponse<IFrequencyFileSaveResponse> = yield roomFrequencyFileSaveRequestService(
            action.payload.roomId,
            action.payload.frequencyFile
        );

        if (saveResponse.data.status) {
            const data = saveResponse.data.data;

            yield put(fileUploadComplete({ uploadId: action.payload.uploadId }));
            yield put(roomFrequencyFileSaveSuccess({
                sweepId: action.payload.sweepId,
                roomId: action.payload.roomId,
                frequencyFile: data.frequencyFile
            }));

        }

    } catch (error: any) {
        yield put(roomFrequencyFileSaveFailure({
            sweepId: action.payload.sweepId,
            roomId: action.payload.roomId,
            error: {
                message: error.response.data.message || 'Error trying to save room frequency file'
            }
        }));
    }
}
function* watchRoomFrequencyFileSaveRequestSaga() {
    yield takeLatest(roomFrequencyFileSaveRequest.toString(), roomFrequencyFileSaveRequestSaga);
}
// FREQUENCY FILE *** DELETE ***
function* roomDeleteFrequencyFileSaga(action: PayloadAction<{
    sweepId: string;
    roomId: string;

}>) {
    try {
        const frequencyFileDeleteResponse: AxiosResponse<IFrequencyFileSaveResponse> = yield roomFrequencyFileDeleteRequestService(
            action.payload.roomId
        );

        if (frequencyFileDeleteResponse.data.status) {
            yield put(roomFrequencyFileDeleteSuccess({
                roomId: action.payload.roomId,
                sweepId: action.payload.sweepId,
            }))
        } else {
            yield put(roomFrequencyFileDeleteFailure({
                roomId: action.payload.roomId,
                sweepId: action.payload.sweepId,
                error: {
                    message: frequencyFileDeleteResponse.data.message || 'Error removing frequency file'
                },
            }))
        }
    } catch (error: any) {
        yield put(roomFloorPlanDeleteFailure({
            roomId: action.payload.roomId,
            sweepId: action.payload.roomId,
            error: {
                message: error.response.data.message || 'Error trying to get the floor plan'
            }
        }));
    }
}
function* watchDeleteFrequencyFileRequestSaga() {
    yield takeLatest(roomFrequencyFileDeleteRequest.toString(), roomDeleteFrequencyFileSaga);
}
// ROOM ITEMS
function* sweepsRoomItemSaveRequestSaga(action: PayloadAction<ISweepRoomItem>) {
    try {
        const payload = action.payload;

        const payloadRoomItem: ISweepSaveRoomItemRequestPayload = {
            name: payload.name,
            amount: payload.amount,
            risk: payload.risk,
            open: payload.open,
            sealed: payload.sealed,
            jeopardized: payload.jeopardized,
            observation: payload.observations || "",
            info: payload.info || "",
            roomId: payload.roomId,
        }

        const roomItemSaveResponse: AxiosResponse<ISweepSaveRoomItemRequestResponse> = yield sweepsRoomItemSaveRequestService(payloadRoomItem, payload.id);

        if (roomItemSaveResponse.data.status) {
            history.back();

            yield put(sweepsSaveRoomItemSuccess({
                ...action.payload,
                id: roomItemSaveResponse.data.data.id,
                name: roomItemSaveResponse.data.data.name,
                amount: roomItemSaveResponse.data.data.amount,
                open: roomItemSaveResponse.data.data.open,
                sealed: roomItemSaveResponse.data.data.sealed,
                jeopardized: roomItemSaveResponse.data.data.jeopardized,
                observations: roomItemSaveResponse.data.data.observation,
                info: roomItemSaveResponse.data.data.info
            }))

            if (!action.payload.id && payload.sweepId) {
                yield delay(50);
                history.push(`/item/${payload.sweepId}/${action.payload.roomId}/${roomItemSaveResponse.data.data.id}`);
            }
        }
        else {
            yield put(sweepsSaveRoomItemFailure({
                error: {
                    message: roomItemSaveResponse.data.message || 'Error trying to save room item'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepsSaveRoomItemFailure({
            error: {
                message: error.response.data.message || 'Error trying to save sweep room'
            }
        }));
    }
}

function* watchSweepRoomItemSaveRequest() {
    yield takeLatest(sweepsSaveRoomItemRequest.type, sweepsRoomItemSaveRequestSaga);
}

function* sweepsRoomItemRemoveRequestSaga(action: PayloadAction<string>) {
    try {
        const roomItemRemoveResponse: AxiosResponse<ISweepSaveRoomRequestResponse> = yield sweepsRoomItemRemoveRequestService(action.payload);

        if (roomItemRemoveResponse.data.status) {
            history.back();

            yield put(sweepRoomItemRemoveSuccess(action.payload))
        }
        else {
            yield put(sweepRoomItemRemoveFailure({
                itemId: action.payload,
                error: {
                    message: roomItemRemoveResponse.data.message || 'Error trying to remove sweep room'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepRoomRemoveFailure({
            roomId: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to remove sweep room'
            }
        }));
    }
}

function* watchSweepRoomItemRemoveRequest() {
    yield takeLatest(sweepRoomItemRemoveRequest.type, sweepsRoomItemRemoveRequestSaga);
}

// *** FREQUENCY ANALYSIS | UPLAOD ***
function* roomFrequencyAnalysisSaveRequestSaga(action: PayloadAction<{
    uploadId: string;
    sweepId: string;
    frequencyAnalysis: string;
    roomId: string;
}>) {
    try {
        const saveResponse: AxiosResponse<IFrequencyAnalysisSaveResponse> = yield roomFrequencyAnalysisSaveRequestService(
            action.payload.roomId,
            action.payload.frequencyAnalysis
        );
        if (saveResponse.data.status) {
            const data = saveResponse.data.data;

            yield put(fileUploadComplete({ uploadId: action.payload.uploadId }));
            yield put(roomFrequencyAnalysisSaveSuccess({
                sweepId: action.payload.sweepId,
                roomId: action.payload.roomId,
                frequencyAnalysis: data.frequencyAnalysis
            }));

        }

    } catch (error: any) {
        yield put(roomFrequencyAnalysisSaveFailure({
            sweepId: action.payload.sweepId,
            roomId: action.payload.roomId,
            error: {
                message: error.response.data.message || 'Error trying to save room frequency analysis'
            }
        }));
    }
}
function* watchRoomFrequencyAnalysisSaveRequestSaga() {
    yield takeLatest(roomFrequencyAnalysisSaveRequest.toString(), roomFrequencyAnalysisSaveRequestSaga);
}

// *** FREQUENCY ANALYSIS | DELETE ***
function* roomDeleteFrequencyAnalysisSaga(action: PayloadAction<{
    sweepId: string;
    roomId: string;

}>) {
    try {
        const frequencyAnalysisDeleteResponse: AxiosResponse<IFrequencyAnalysisDeleteResponse> = yield roomFrequencyAnalysisDeleteRequestService(
            action.payload.roomId
        );

        if (frequencyAnalysisDeleteResponse.data.status) {
            yield put(roomFrequencyAnalysisDeleteSuccess({
                roomId: action.payload.roomId,
                sweepId: action.payload.sweepId,
            }))
        } else {
            yield put(roomFrequencyAnalysisDeleteFailure({
                roomId: action.payload.roomId,
                sweepId: action.payload.sweepId,
                error: {
                    message: frequencyAnalysisDeleteResponse.data.message || 'Error removing frequency analysis'
                },
            }))
        }
    } catch (error: any) {
        yield put(roomFrequencyAnalysisDeleteFailure({
            roomId: action.payload.roomId,
            sweepId: action.payload.roomId,
            error: {
                message: error.response.data.message || 'Error trying to get the frequency analysis'
            }
        }));
    }
}
function* watchDeleteFrequencyAnalysisRequestSaga() {
    yield takeLatest(roomFrequencyAnalysisDeleteRequest.toString(), roomDeleteFrequencyAnalysisSaga);
}

// *** SWEEP WORK REGISTERS || ADD ***
function* sweepsWorkRegisterSaveRequestSaga(action: PayloadAction<ISweepTime>) {
    try {
        const payloadRegister: ISweepWorkRegisterRequestPayload = {
            beginingAt: action.payload.beginingAt,
            endingAt: action.payload.endingAt,
            observations: action.payload.observation,
            sweepId: action.payload.sweepId
        }
        const registerSaveResponse: AxiosResponse<ISweepWorkRegisterRequestResponse, ISweepWorkRegisterRequestResponse> = yield sweepsWorkRegisterSaveRequestService(payloadRegister, action.payload.sweepId);

        if (registerSaveResponse.data.status) {
            history.back();

            yield put(sweepsSaveWorkRegisterSuccess({
                ...action.payload,
                id: registerSaveResponse.data.data.id || action.payload.id,
                beginingAt: new Date(registerSaveResponse.data.data.beginingAt),
                endingAt: new Date(registerSaveResponse.data.data.endingAt),
                observation: registerSaveResponse.data.data.observations,
                sweepId: action.payload.sweepId

            }))

            yield delay(50);

        }
        else {
            yield put(sweepsSaveWorkRegisterFailure({
                error: {
                    message: registerSaveResponse.data.message || 'Error trying to save register work '
                }

            }));

        }
    }
    catch (error: any) {
        yield put(sweepsSaveWorkRegisterFailure({
            error: {
                message: error.response.data.message || 'Error trying to save register work'
            }
        }));
    }
}

function* watchSweepWorkRegisterSaveRequest() {
    yield takeLatest(sweepsSaveWorkRegisterRequest.type, sweepsWorkRegisterSaveRequestSaga);
}

// *** SWEEP WORK REGISTERS || REMOVE ***
function* sweepsWorkRegisterRemoveRequestSaga(action: PayloadAction<string>) {
    try {
        const registerRemoveResponse: AxiosResponse<ISweepWorkRegisterRemoveRequestResponse> = yield sweepsWorkRegisterRemoveRequestService(action.payload);

        if (registerRemoveResponse.data.status) {
            yield put(sweepWorkRegisterRemoveSuccess(action.payload))
        }
        else {
            yield put(sweepWorkRegisterRemoveFailure({
                workRegisterId: action.payload,
                error: {
                    message: registerRemoveResponse.data.message || 'Error trying to remove work register'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(sweepWorkRegisterRemoveFailure({
            workRegisterId: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to remove work register'
            }
        }));
    }
}

function* watchSweepWorkRegisterRemoveRequest() {
    yield takeLatest(sweepWorkRegisterRemoveRequest.type, sweepsWorkRegisterRemoveRequestSaga);
}

// *** ROOM WORK REGISTERS || ADD ***
function* roomsWorkRegisterSaveRequestSaga(action: PayloadAction<ISweepRoomTime>) {
    try {
        const payloadRegister: IRoomWorkRegisterRequestPayload = {
            beginingAt: action.payload.beginingAt,
            endingAt: action.payload.endingAt,
            observations: action.payload.observation,
            roomId: action.payload.roomId || '',
        }
        const registerSaveResponse: AxiosResponse<IRoomWorkRegisterRequestResponse, IRoomWorkRegisterRequestResponse> = yield roomsWorkRegisterSaveRequestService(payloadRegister);

        if (registerSaveResponse.data.status) {
            history.back();

            yield put(roomSaveWorkRegisterSuccess({
                ...action.payload,
                id: registerSaveResponse.data.data.id || action.payload.id,
                beginingAt: new Date(registerSaveResponse.data.data.beginingAt),
                endingAt: new Date(registerSaveResponse.data.data.endingAt),
                observation: registerSaveResponse.data.data.observations,
                roomId: action.payload.roomId,
                sweepId: action.payload.sweepId,
            }))

            yield delay(50);

        }
        else {
            yield put(roomSaveWorkRegisterFailure({
                error: {
                    message: registerSaveResponse.data.message || 'Error trying to save register work '
                }

            }));
        }
    }
    catch (error: any) {
        yield put(roomSaveWorkRegisterFailure({
            error: {
                message: error.response.data.message || 'Error trying to save register work'
            }
        }));
    }
}

function* watchRoomWorkRegisterSaveRequest() {
    yield takeLatest(roomSaveWorkRegisterRequest.type, roomsWorkRegisterSaveRequestSaga);
}

// *** SWEEP WORK REGISTERS || REMOVE ***
function* roomWorkRegisterRemoveRequestSaga(action: PayloadAction<string>) {
    try {
        const registerRemoveResponse: AxiosResponse<IRoomWorkRegisterRemoveRequestResponse> = yield roomsWorkRegisterRemoveRequestService(action.payload);

        if (registerRemoveResponse.data.status) {
            yield put(roomWorkRegisterRemoveSuccess(action.payload))
        }
        else {
            yield put(roomWorkRegisterRemoveFailure({
                workRegisterId: action.payload,
                error: {
                    message: registerRemoveResponse.data.message || 'Error trying to remove work register'
                }
            }));
        }
    }
    catch (error: any) {
        yield put(roomWorkRegisterRemoveFailure({
            workRegisterId: action.payload,
            error: {
                message: error.response.data.message || 'Error trying to remove work register'
            }
        }));
    }
}

function* watchRoomWorkRegisterRemoveRequest() {
    yield takeLatest(roomWorkRegisterRemoveRequest.type, roomWorkRegisterRemoveRequestSaga);
}



export default all([
    watchSweepsListRequestSaga(),
    watchSweepSaveRequestSaga(),
    watchSweepRoomSaveRequest(),
    watchSweepRoomRemoveRequest(),
    watchRoomPhotoExportRequest(),
    watchRoomPhotoFlirExportRequest(),
    watchRoomFloorPLanSaveRequestSaga(),
    watchDeleteFloorPlanRequestSaga(),
    watchRoomFrequencyFileSaveRequestSaga(),
    watchDeleteFrequencyFileRequestSaga(),
    watchSweepRemoveRequest(),
    watchSweepGuestSaveRequest(),
    watchSweepGuestRemoveRequest(),
    watchSweepRoomItemSaveRequest(),
    watchSweepRoomItemRemoveRequest(),
    watchSweepExportRequest(),
    watchSweepArquiveRequestSaga(),
    watchSweepUnarquiveRequestSaga(),
    watchRoomVideoExportRequest(),
    watchRoomFrequencyAnalysisSaveRequestSaga(),
    watchDeleteFrequencyAnalysisRequestSaga(),
    watchSweepWorkRegisterSaveRequest(),
    watchSweepWorkRegisterRemoveRequest(),
    watchRoomWorkRegisterSaveRequest(),
    watchRoomWorkRegisterRemoveRequest()
])